import React from 'react';
import { useState, useEffect } from 'react';
import api from '../services/api';

/*
  Form024Modal — Consentimiento informado oficial MSP (Formulario 024)
  Componente controlado, sin ruta propia — mismo patrón que RecetaModal.jsx.
  Flota encima de la pantalla que lo invoque (hoy: Odontograma.jsx desde el
  panel de la pieza), sin navegar ni perder el contexto abierto detrás.

  Props:
    codPacienteInicial   — COD del paciente (obligatorio)
    numeroPiezaInicial   — número FDI de la pieza, si se abre desde una pieza (opcional)
    procedimientoInicial — precarga NOMBRE_PROCEDIMIENTO (ej. TRATAMIENTO de la pieza)
    onGuardado()         — se llama tras generar el PDF con éxito
    onCerrar()           — cierra el modal sin guardar
*/
export default function Form024Modal({
  codPacienteInicial, numeroPiezaInicial, procedimientoInicial = '',
  onGuardado, onCerrar,
}) {
  const [form, setForm] = useState({
    NOMBRE_PROCEDIMIENTO: procedimientoInicial,
    SERVICIO: '',
    TIPO_ATENCION: 'AMBULATORIO',
    DIAGNOSTICO: '',
    COD_CIE10: '',
    EN_QUE_CONSISTE: '',
    COMO_SE_REALIZA: '',
    DURACION_ESTIMADA: '',
    BENEFICIOS: '',
    RIESGOS_FRECUENTES: '',
    RIESGOS_POCO_FRECUENTES: '',
    RIESGOS_ESPECIFICOS: '',
    ALTERNATIVAS: '',
    MANEJO_POSTERIOR: '',
    CONSECUENCIAS_NO_REALIZAR: '',
    TIPO_DECISION: 'ACEPTA',
  });
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const [cieQuery, setCieQuery] = useState('');
  const [cieSugs, setCieSugs] = useState([]);
  const [cieDesc, setCieDesc] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [plantillas, setPlantillas] = useState([]);
  const [plantillaSel, setPlantillaSel] = useState('');
  const [guardarPlantilla, setGuardarPlantilla] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState('');

  const [grafico, setGrafico] = useState(null);
  const [graficoPreview, setGraficoPreview] = useState(null);

  const CAMPOS_PLANTILLA = [
    'SERVICIO', 'EN_QUE_CONSISTE', 'COMO_SE_REALIZA', 'DURACION_ESTIMADA', 'BENEFICIOS',
    'RIESGOS_FRECUENTES', 'RIESGOS_POCO_FRECUENTES', 'RIESGOS_ESPECIFICOS',
    'ALTERNATIVAS', 'MANEJO_POSTERIOR', 'CONSECUENCIAS_NO_REALIZAR',
  ];

  const [plantillasError, setPlantillasError] = useState(false);

  useEffect(() => {
    api.get('/historia/plantillas-form024')
      .then(({ data }) => setPlantillas(data || []))
      .catch(() => { setPlantillas([]); setPlantillasError(true); });
  }, []);

  const aplicarPlantilla = codPlantilla => {
    setPlantillaSel(codPlantilla);
    if (!codPlantilla) return;
    const p = plantillas.find(x => String(x.COD) === String(codPlantilla));
    if (!p) return;
    setForm(f => {
      const actualizado = { ...f };
      CAMPOS_PLANTILLA.forEach(campo => { actualizado[campo] = p[campo] || ''; });
      return actualizado;
    });
  };

  const elegirGrafico = file => {
    setGrafico(file);
    setGraficoPreview(file ? URL.createObjectURL(file) : null);
  };

  useEffect(() => {
    if (!cieQuery.trim()) { setCieSugs([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/historia/buscar/cie10?q=${encodeURIComponent(cieQuery)}`);
        setCieSugs(data || []);
      } catch (e) { setCieSugs([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [cieQuery]);

  const elegirCie = c => {
    set('COD_CIE10', c.COD_CIE10);
    setCieDesc(c.DIAGNOSTICO);
    setCieQuery('');
    setCieSugs([]);
  };

  const guardar = async () => {
    setError('');
    if (!form.NOMBRE_PROCEDIMIENTO.trim()) { setError('Describe el procedimiento a realizar'); return; }
    if (guardarPlantilla && !nombrePlantilla.trim()) { setError('Escribe un nombre para la plantilla'); return; }
    setGuardando(true);
    try {
      const { data } = await api.post('/historia/form024', {
        COD_PACIENTE: codPacienteInicial,
        NUMERO_PIEZA: numeroPiezaInicial || null,
        ...form,
      });

      if (grafico) {
        const fd = new FormData();
        fd.append('grafico', grafico);
        await api.post(`/historia/form024/${data.COD}/grafico`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (guardarPlantilla) {
        const plantillaBody = { NOMBRE: nombrePlantilla.trim() };
        CAMPOS_PLANTILLA.forEach(campo => { plantillaBody[campo] = form[campo]; });
        await api.post('/historia/plantillas-form024', plantillaBody).catch(() => {});
      }

      const { data: pdfBytes } = await api.get(`/historia/form024/${data.COD}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
      window.open(url, '_blank');
      onGuardado?.();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo generar el consentimiento');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="f024-overlay">
        <div className="f024-card" onClick={e => e.stopPropagation()}>
          <div className="f024-hdr">
            <span><i className="ti ti-file-check" aria-hidden="true"></i> Consentimiento informado — Formulario 024</span>
            <button className="f024-close" onClick={onCerrar} title="Cerrar"><i className="ti ti-x" aria-hidden="true"></i></button>
          </div>

          {numeroPiezaInicial && (
            <div className="f024-badge-pieza"><i className="ti ti-dental" aria-hidden="true"></i> Pieza {numeroPiezaInicial}</div>
          )}

          <div className="f024-body">
            {error && <div className="f024-error"><i className="ti ti-alert-circle" aria-hidden="true"></i> {error}</div>}

            <div className="f024-plantilla-bar">
              <i className="ti ti-clipboard-text" aria-hidden="true"></i>
              {plantillasError ? (
                <span className="f024-plantilla-msg">No se pudo cargar la lista de plantillas (revisa que el servidor esté actualizado).</span>
              ) : plantillas.length === 0 ? (
                <span className="f024-plantilla-msg">Aún no tienes ninguna plantilla guardada — completa el formulario y marca "Guardar como plantilla" abajo para crear la primera.</span>
              ) : (
                <select className="form-control" value={plantillaSel} onChange={e => aplicarPlantilla(e.target.value)}>
                  <option value="">Usar plantilla existente...</option>
                  {plantillas.map(p => <option key={p.COD} value={p.COD}>{p.NOMBRE}</option>)}
                </select>
              )}
            </div>

            <div className="mo-sec">Procedimiento a realizar *</div>
            <input className="form-control" placeholder="Ej: Endodoncia pieza 16"
              value={form.NOMBRE_PROCEDIMIENTO} onChange={e => set('NOMBRE_PROCEDIMIENTO', e.target.value)} />

            <div className="f024-row2">
              <div>
                <div className="mo-sec">Tipo de atención</div>
                <div className="f024-radios">
                  {['AMBULATORIO', 'HOSPITALIZACION'].map(t => (
                    <button key={t} type="button" className={`od-chip ${form.TIPO_ATENCION === t ? 'act' : ''}`}
                      onClick={() => set('TIPO_ATENCION', t)}>
                      {t === 'AMBULATORIO' ? 'Ambulatorio' : 'Hospitalización'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="mo-sec">Servicio</div>
                <input className="form-control" placeholder="Ej: Odontología"
                  value={form.SERVICIO} onChange={e => set('SERVICIO', e.target.value)} />
              </div>
            </div>

            <div className="mo-sec" style={{ marginTop: 10 }}>Diagnóstico</div>
            <input className="form-control" placeholder="Ej: Caries dental profunda"
              value={form.DIAGNOSTICO} onChange={e => set('DIAGNOSTICO', e.target.value)} />

            <div className="mo-sec" style={{ marginTop: 10 }}>Diagnóstico CIE-10</div>
            {form.COD_CIE10 ? (
              <div className="f024-cie-chip">
                <i className="ti ti-stethoscope" aria-hidden="true"></i>
                {form.COD_CIE10} · {cieDesc || 'Diagnóstico seleccionado'}
                <button onClick={() => { set('COD_CIE10', ''); setCieDesc(''); }} title="Quitar">
                  <i className="ti ti-x" aria-hidden="true"></i>
                </button>
              </div>
            ) : (
              <div className="f024-cie-buscador">
                <input className="form-control" placeholder="Buscar por código o nombre..."
                  value={cieQuery} onChange={e => setCieQuery(e.target.value)} />
                {cieSugs.length > 0 && (
                  <div className="f024-cie-sugs">
                    {cieSugs.map(c => (
                      <div key={c.COD} className="f024-cie-sug" onClick={() => elegirCie(c)}>
                        <b>{c.COD_CIE10}</b> · {c.DIAGNOSTICO}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="f024-row2">
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>En qué consiste</div>
                <textarea className="form-control" rows={2}
                  value={form.EN_QUE_CONSISTE} onChange={e => set('EN_QUE_CONSISTE', e.target.value)} />
              </div>
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Cómo se realiza</div>
                <textarea className="form-control" rows={2}
                  value={form.COMO_SE_REALIZA} onChange={e => set('COMO_SE_REALIZA', e.target.value)} />
              </div>
            </div>

            <div className="f024-row2">
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Duración estimada</div>
                <input className="form-control" placeholder="Ej: 45 minutos"
                  value={form.DURACION_ESTIMADA} onChange={e => set('DURACION_ESTIMADA', e.target.value)} />
              </div>
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Beneficios</div>
                <input className="form-control" placeholder="Ej: Alivio del dolor, conserva la pieza"
                  value={form.BENEFICIOS} onChange={e => set('BENEFICIOS', e.target.value)} />
              </div>
            </div>

            <div className="mo-sec" style={{ marginTop: 10 }}>Riesgos frecuentes</div>
            <textarea className="form-control" rows={2}
              value={form.RIESGOS_FRECUENTES} onChange={e => set('RIESGOS_FRECUENTES', e.target.value)} />

            <div className="f024-row2">
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Riesgos poco frecuentes</div>
                <textarea className="form-control" rows={2}
                  value={form.RIESGOS_POCO_FRECUENTES} onChange={e => set('RIESGOS_POCO_FRECUENTES', e.target.value)} />
              </div>
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Riesgos específicos</div>
                <textarea className="form-control" rows={2}
                  value={form.RIESGOS_ESPECIFICOS} onChange={e => set('RIESGOS_ESPECIFICOS', e.target.value)} />
              </div>
            </div>

            <div className="mo-sec" style={{ marginTop: 10 }}>Alternativas</div>
            <textarea className="form-control" rows={2}
              value={form.ALTERNATIVAS} onChange={e => set('ALTERNATIVAS', e.target.value)} />

            <div className="f024-row2">
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Manejo posterior</div>
                <textarea className="form-control" rows={2}
                  value={form.MANEJO_POSTERIOR} onChange={e => set('MANEJO_POSTERIOR', e.target.value)} />
              </div>
              <div>
                <div className="mo-sec" style={{ marginTop: 10 }}>Consecuencias de no realizarlo</div>
                <textarea className="form-control" rows={2}
                  value={form.CONSECUENCIAS_NO_REALIZAR} onChange={e => set('CONSECUENCIAS_NO_REALIZAR', e.target.value)} />
              </div>
            </div>

            <div className="mo-sec" style={{ marginTop: 12 }}>Gráfico de la intervención (opcional)</div>
            {graficoPreview ? (
              <div className="f024-grafico-preview">
                <img src={graficoPreview} alt="Gráfico de la intervención" />
                <button onClick={() => elegirGrafico(null)} title="Quitar imagen"><i className="ti ti-x" aria-hidden="true"></i></button>
              </div>
            ) : (
              <label className="f024-grafico-btn">
                <i className="ti ti-photo-plus" aria-hidden="true"></i> Adjuntar imagen o diagrama para el paciente
                <input type="file" accept="image/*" hidden
                  onChange={e => { if (e.target.files[0]) elegirGrafico(e.target.files[0]); e.target.value = ''; }} />
              </label>
            )}

            <div className="mo-sec" style={{ marginTop: 12 }}>Decisión del paciente</div>
            <div className="f024-radios">
              {[
                { cod: 'ACEPTA', label: 'Acepta', icon: 'ti-check' },
                { cod: 'RECHAZA', label: 'Rechaza', icon: 'ti-x' },
              ].map(d => (
                <button key={d.cod} type="button" className={`od-chip ${form.TIPO_DECISION === d.cod ? 'act' : ''}`}
                  onClick={() => set('TIPO_DECISION', d.cod)}>
                  <i className={`ti ${d.icon}`} aria-hidden="true"></i> {d.label}
                </button>
              ))}
            </div>

            <div className="f024-guardar-plantilla">
              <label>
                <input type="checkbox" checked={guardarPlantilla} onChange={e => setGuardarPlantilla(e.target.checked)} />
                Guardar esta combinación como plantilla para la próxima vez
              </label>
              {guardarPlantilla && (
                <input className="form-control" placeholder="Nombre de la plantilla (ej: Endodoncia)"
                  value={nombrePlantilla} onChange={e => setNombrePlantilla(e.target.value)} />
              )}
            </div>

            <button className="btn-mo-pri f024-guardar" disabled={guardando} onClick={guardar}>
              <i className="ti ti-file-check" aria-hidden="true"></i> {guardando ? 'Generando...' : 'Generar consentimiento (PDF)'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const css = `
.f024-overlay { position:fixed; inset:0; background:rgba(15,20,30,.45); display:flex; align-items:flex-start; justify-content:center; padding:32px 16px; z-index:200; overflow-y:auto; }
.f024-card { background:var(--surface); border-radius:14px; width:100%; max-width:640px; overflow:hidden; box-shadow:0 12px 32px rgba(0,0,0,.18); }
.f024-hdr { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; background:var(--navy); color:#fff; font-size:14px; font-weight:700; }
.f024-hdr i { margin-right:6px; }
.f024-close { background:none; border:none; color:#fff; cursor:pointer; font-size:16px; opacity:.85; }
.f024-close:hover { opacity:1; }
.f024-badge-pieza { display:inline-flex; align-items:center; gap:6px; font-size:11px; font-weight:700; color:var(--navy); background:var(--blue-lt); margin:12px 18px 0; padding:5px 12px; border-radius:20px; width:fit-content; }
.f024-body { padding:16px 18px 20px; max-height:70vh; overflow-y:auto; }
.f024-row2 { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.f024-radios { display:flex; flex-wrap:wrap; gap:6px; }
.f024-error { background:var(--red-lt); color:var(--red); border:1px solid var(--red); border-radius:8px; padding:8px 12px; font-size:12px; margin-bottom:10px; display:flex; align-items:center; gap:6px; }
.f024-cie-chip { display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:600; color:var(--navy); background:var(--blue-lt); border:1px solid var(--navy-lt); border-radius:20px; padding:6px 8px 6px 12px; }
.f024-cie-chip button { background:none; border:none; color:inherit; cursor:pointer; padding:2px; opacity:.7; }
.f024-cie-chip button:hover { opacity:1; }
.f024-cie-buscador { position:relative; }
.f024-cie-sugs { position:absolute; top:100%; left:0; right:0; background:var(--surface); border:1px solid var(--border); border-radius:9px; margin-top:4px; max-height:180px; overflow-y:auto; z-index:5; box-shadow:0 6px 16px rgba(0,0,0,.1); }
.f024-cie-sug { padding:8px 12px; font-size:12.5px; cursor:pointer; border-bottom:1px solid var(--bg); color:var(--text); }
.f024-cie-sug:hover { background:var(--bg); }
.f024-guardar { width:100%; margin-top:16px; }
.f024-plantilla-bar { display:flex; align-items:center; gap:8px; background:var(--bg); border:1px dashed var(--border); border-radius:9px; padding:8px 10px; margin-bottom:14px; color:var(--text2); }
.f024-plantilla-bar select { margin:0; }
.f024-plantilla-msg { font-size:11.5px; line-height:1.4; }
.f024-grafico-btn { display:flex; align-items:center; gap:8px; background:var(--blue-lt); color:var(--navy); border:1px dashed var(--navy-lt); border-radius:9px; padding:10px 14px; font-size:12.5px; cursor:pointer; }
.f024-grafico-preview { position:relative; display:inline-block; }
.f024-grafico-preview img { max-width:220px; max-height:140px; border-radius:9px; border:1px solid var(--border); display:block; }
.f024-grafico-preview button { position:absolute; top:-6px; right:-6px; width:20px; height:20px; border-radius:50%; background:var(--red); color:#fff; border:none; font-size:11px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.f024-guardar-plantilla { margin-top:14px; padding-top:12px; border-top:1px solid var(--border); display:flex; flex-direction:column; gap:8px; }
.f024-guardar-plantilla label { display:flex; align-items:center; gap:8px; font-size:12px; color:var(--text2); cursor:pointer; }
`;
